import { AdvancedAPICourse } from './types';

export const ADVANCED_API_COURSES: AdvancedAPICourse[] = [
  {
    id: 'graphql-federation-advanced',
    title: 'GraphQL Federation & Advanced Patterns',
    description: 'Master GraphQL federation, schema stitching, and advanced patterns for building scalable, distributed GraphQL architectures.',
    level: 'advanced',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    tags: ['GraphQL', 'Federation', 'Microservices', 'Schema Design', 'Advanced Patterns'],
    prerequisites: ['GraphQL fundamentals', 'API design experience', 'Microservices understanding'],
    estimatedHours: 8,
    objectives: [
      'Implement GraphQL federation at scale',
      'Design distributed schema architectures',
      'Optimize performance for federated graphs',
      'Implement advanced GraphQL patterns'
    ],
    learningOutcomes: [
      'Build federated GraphQL architectures',
      'Design scalable schema compositions',
      'Implement advanced resolver patterns',
      'Optimize query performance and caching',
      'Handle error boundaries in federated systems',
      'Implement real-time subscriptions at scale'
    ],
    modules: [
      {
        id: 'graphql-federation-basics',
        title: 'GraphQL Federation Fundamentals',
        description: 'Learn the basics of GraphQL federation',
        content: 'GraphQL federation enables building a unified API from multiple GraphQL services...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '2 hours',
        learningObjectives: [
          'Understand federation architecture',
          'Set up Apollo Federation',
          'Design federated schemas'
        ],
        codeExamples: [
          {
            title: 'Federated Schema Example',
            code: 'type User @key(fields: "id") {\n  id: ID!\n  username: String!\n  email: String!\n}\n\ntype Post @key(fields: "id") {\n  id: ID!\n  title: String!\n  content: String!\n  author: User @provides(fields: "username")\n}\n\nextend type User @key(fields: "id") {\n  id: ID! @external\n  posts: [Post!]!\n}',
            explanation: 'Example of federated GraphQL schema with entity extensions.',
            language: 'graphql'
          }
        ]
      },
      {
        id: 'advanced-schema-design',
        title: 'Advanced Schema Design Patterns',
        description: 'Master complex schema composition techniques',
        content: 'Learn advanced patterns for designing scalable GraphQL schemas...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'federation-performance',
        title: 'Federation Performance Optimization',
        description: 'Optimize federated GraphQL performance',
        content: 'Implement caching, batching, and optimization strategies...',
        duration: '2 hours',
        difficulty: 'expert',
        order: 3
      },
      {
        id: 'graphql-subscriptions',
        title: 'Real-time Subscriptions and Federation',
        description: 'Implement real-time features in federated graphs',
        content: 'Build real-time subscriptions across federated services...',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'federation-monitoring',
        title: 'Monitoring and Observability',
        description: 'Monitor federated GraphQL systems',
        content: 'Implement comprehensive monitoring for federated architectures...',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 5
      }
    ],
    type: 'advanced-api'
  },
  {
    id: 'grpc-high-performance-apis',
    title: 'gRPC & High-Performance APIs',
    description: 'Build ultra-fast, type-safe APIs using gRPC, Protocol Buffers, and advanced streaming patterns for microservices communication.',
    level: 'advanced',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    tags: ['gRPC', 'Protocol Buffers', 'High Performance', 'Microservices', 'Streaming'],
    prerequisites: ['API development experience', 'Understanding of HTTP/2', 'Microservices concepts'],
    estimatedHours: 6,
    objectives: [
      'Build high-performance gRPC APIs',
      'Implement streaming patterns',
      'Optimize gRPC for production',
      'Design efficient Protocol Buffer schemas'
    ],
    learningOutcomes: [
      'Create efficient gRPC service definitions',
      'Implement unary, server, client, and bidirectional streaming',
      'Design optimal Protocol Buffer schemas',
      'Handle authentication and authorization',
      'Implement load balancing and service discovery',
      'Monitor and debug gRPC services'
    ],
    modules: [
      {
        id: 'grpc-fundamentals',
        title: 'gRPC and Protocol Buffers Fundamentals',
        description: 'Learn gRPC basics and Protocol Buffer design',
        content: 'gRPC is a high-performance RPC framework that uses HTTP/2...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '1.5 hours',
        learningObjectives: [
          'Understand gRPC architecture',
          'Design Protocol Buffer schemas',
          'Generate client and server code'
        ],
        codeExamples: [
          {
            title: 'Protocol Buffer Definition',
            code: 'syntax = "proto3";\n\npackage user;\n\nservice UserService {\n  rpc GetUser(GetUserRequest) returns (User);\n  rpc ListUsers(ListUsersRequest) returns (stream User);\n  rpc CreateUser(CreateUserRequest) returns (User);\n}\n\nmessage User {\n  int32 id = 1;\n  string name = 2;\n  string email = 3;\n  repeated string roles = 4;\n}\n\nmessage GetUserRequest {\n  int32 id = 1;\n}',
            explanation: 'Protocol Buffer definition for a user service with streaming.',
            language: 'protobuf'
          }
        ]
      },
      {
        id: 'grpc-streaming-patterns',
        title: 'Advanced Streaming Patterns',
        description: 'Implement all types of gRPC streaming',
        content: 'Master unary, server streaming, client streaming, and bidirectional streaming...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'grpc-production-optimization',
        title: 'Production Optimization',
        description: 'Optimize gRPC for production environments',
        content: 'Learn performance tuning, load balancing, and production best practices...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'grpc-security-auth',
        title: 'Security and Authentication',
        description: 'Secure gRPC services with authentication',
        content: 'Implement TLS, JWT, and custom authentication mechanisms...',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 4
      }
    ],
    type: 'advanced-api'
  }
];